/* eslint-disable */
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Stack,
  Table,
  Button,
  Container,
  TableBody,
  Typography,
  TableContainer,
  TablePagination,
  Snackbar,
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import useAPI from "../../../hooks/useAPI";

import DataTableRow from "../data-table-row";
import DataTableHead from "../data-table-head";
import DataTableToolbar from "../data-table-toolbar";
import { applyFilter, getComparator } from "../../user/utils";
import Scrollbar from "../../../components/scrollbar";

export default function DataViewPage() {
  const api = useAPI();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterName, setFilterName] = useState("");
  const [quickSearchTerm, setQuickSearchTerm] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deleteDataId, setDeleteDataId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.data.getAll(fromDate, toDate, filterName, quickSearchTerm);
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [api.data, fromDate, toDate, filterName, quickSearchTerm]);

  useEffect(() => {
    getData();
  }, [fromDate, toDate, filterName, quickSearchTerm]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await api.data.export(fromDate, toDate);

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "data-export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage("Data exported successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === "asc";
    if (id !== "") {
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(id);
    }
  };

  const handleDeleteDataClick = (id) => {
    setDeleteDataId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteData = async () => {
    try {
      const response = await api.data.delete(deleteDataId);
      if (response.data.success) {
        setSuccessMessage("Data deleted successfully");
        setSnackbarOpen(true);
        getData();
      }
    } catch (error) {
      console.error("Error deleting data:", error);
    }
    setDeleteDialogOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
    setPage(0);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
    setPage(0);
  };

  const handleFilterNameChange = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleQuickSearchChange = (term) => {
    setQuickSearchTerm(term);
    setPage(0);
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setFilterName("");
    setQuickSearchTerm("");
    setPage(0);
    getData();
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const dataFiltered = useMemo(() => {
    return applyFilter({
      inputData: data,
      comparator: getComparator(order, orderBy),
    });
  }, [data, order, orderBy]);

  return (
    <Container>
      <Stack
        direction="row"
        mt={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h4">Logs Management</Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? (
              <>
                <CircularProgress size={18} color="inherit" />
                &nbsp; Exporting...
              </>
            ) : (
              "Export Data"
            )}
          </Button>
        </Box>
      </Stack>

      <Card>
        <DataTableToolbar
          fromDate={fromDate}
          toDate={toDate}
          filterName={filterName}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
          onFilterName={handleFilterNameChange}
          onQuickSearch={handleQuickSearchChange}
          onReset={handleReset}
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Scrollbar>
            <TableContainer sx={{ overflow: "unset" }}>
              <Table sx={{ minWidth: 800 }}>
                <DataTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                />
                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <DataTableRow
                        key={row._id}
                        id={row._id}
                        data={row.data}
                        createdAt={row.createdAt}
                        handleDeleteDataClick={handleDeleteDataClick}
                      />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        )}

        <TablePagination
          page={page}
          component="div"
          count={dataFiltered.length}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[5, 10, 25]}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}